import { useState } from 'react'
import { useParams } from 'router-v6'
import { v4 } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import { usePubNub } from 'v2/shared/hooks/use-pubnub'
import { playgroundService } from 'v2/shared/services/playground-service'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import type { ConvChannels } from 'v2/types/api/conversations'
import type { RUserProfile } from 'v2/types/api/settings'
import { getLocalAuth } from 'v2/utils'
import { agentLogger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import { usePlaygroundStreamTransport } from '../../hooks/use-playground-stream-transport'
import { useFlowRunStore } from '../store'
import { createAsyncMessagePayload } from '../types'

// The response interceptor rejects a session 401 with a plain Error after
// calling logout, so the AxiosError shape is not always available.
const isUnauthorized = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 401 ||
  String(error).includes('401 Unauthorized')

export const useSendMessage = () => {
  const [isSending, setIsSending] = useState(false)
  const { publishMutation } = usePubNub()
  const playgroundStreamTransportEnabled = usePlaygroundStreamTransport()
  const { currentRunId, executionHistory, asyncAgentMeta } = useFlowRunStore()
  const { id } = useParams()
  const { from, to, channelName, chatChannel } = asyncAgentMeta

  const sendMessage = (text: string, uploadedFile?: RFileUploadResponse | null) => {
    const trimmedMessage = text.trim()
    const hasContent = trimmedMessage || uploadedFile

    if (hasContent && !isSending) {
      setIsSending(true)

      const isFirstMessage = executionHistory.every((event) =>
        event.chunks?.every((chunk) => chunk.sender !== 'user'),
      )

      // Create message payload (supports both text and media messages)
      const messagePayload = createAsyncMessagePayload(
        {
          authId:
            queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)?.organizations[0]
              .org_auth_id || '',
          messageUUID: v4(),
          from,
          to,
          body: trimmedMessage,
          text: trimmedMessage,
          phloId: id || '',
          aomUUID: getLocalAuth().aom_uuid,
          isFirstMessage,
          // Include file attributes for media messages (WhatsApp only)
          ...(uploadedFile &&
            (channelName === 'whatsapp' || channelName === 'chat') && {
              fileAttributes: {
                file_url: uploadedFile.file_url,
                file_type: uploadedFile.file_type,
                file_id: uploadedFile.file_id,
                file_name: uploadedFile.file_name,
                file_size: uploadedFile.file_size,
              },
              mimeType: uploadedFile.file_type,
            }),
        },
        channelName as ConvChannels,
      )
      agentLogger.debug('messagePayload', messagePayload)

      // Chat playground still rides the chat stack over PubNub (its reply path
      // is separate from HandlePlaygroundReply) — only SMS/WhatsApp move.
      if (playgroundStreamTransportEnabled && channelName !== 'chat') {
        playgroundService
          .sendMessage(messagePayload, currentRunId || undefined)
          .catch((error: unknown) => {
            agentLogger.error('playground message send failed', error)
            // A 401 already triggered the global logout in the response
            // interceptor; "try again" would contradict the redirect in flight.
            if (isUnauthorized(error)) return
            errorToast({
              title: 'Message not delivered',
              description: 'The playground could not send your message. Try again.',
            })
          })
          .finally(() => {
            setTimeout(() => setIsSending(false), 1000)
          })
        return
      }

      publishMutation.mutate(
        {
          channel: chatChannel || currentRunId || '',
          message: messagePayload,
        },
        {
          onSuccess: () => {
            setTimeout(() => setIsSending(false), 1000)
          },
          onError: () => {
            setTimeout(() => setIsSending(false), 1000)
          },
        },
      )
    }
  }

  return { sendMessage, isSending }
}
